import { type Color, type Game, type Vertex } from '@baduk/engine';
import { evaluateMoves } from '@baduk/bots';
import { gradeFor } from './review.js';

/**
 * Live feedback on a point before it is played.
 *
 * This is the most powerful crutch in the app and the one to be most careful
 * with. Everything else the coach does makes a learner think first: hints start
 * vague, observations state facts without prescribing, review waits until the
 * game is over. Feedback on hover answers "is this good?" before the question
 * has really been asked, and a learner can sweep the board with it until it says
 * Excellent without ever working out why.
 *
 * So it is off by default, and it is graded on exactly the thresholds the
 * post-game review uses. A move called Bad here is a blunder there — the two
 * cannot drift apart and tell a learner different stories about the same move.
 *
 * The grades are relative to the best move available, never absolute. The
 * evaluator's units are not points, and only the ordering means anything.
 */

export type MoveQuality = 'excellent' | 'good' | 'poor' | 'bad';

/** Illegal points get feedback too — *why* you cannot play somewhere is a lesson. */
export type HoverQuality = MoveQuality | 'illegal';

export interface MoveFeedback {
  readonly vertex: Vertex;
  readonly quality: HoverQuality;
  /** One word, for the badge. */
  readonly label: string;
  /** The coach's judgement of the move, in a sentence. */
  readonly note: string;
  /** The evaluator's own reasoning, the same sentence the bots play by. */
  readonly reason: string;
  /** How far short of the best move, in evaluator units. Absent when illegal. */
  readonly lostValue?: number;
  /** 1 means the coach would play here itself. */
  readonly rank?: number;
}

const LABELS: Record<HoverQuality, string> = {
  excellent: 'Excellent',
  good: 'Good',
  poor: 'Poor',
  bad: 'Bad',
  illegal: 'Illegal',
};

/**
 * Four bands, mapped from the review's five grades so the two always agree.
 * Inaccuracies and mistakes both read as Poor: the difference between them
 * matters when studying a finished game and not when choosing a move.
 */
function qualityFor(lostValue: number): MoveQuality {
  switch (gradeFor(lostValue)) {
    case 'good':
      return 'excellent';
    case 'fine':
      return 'good';
    case 'inaccuracy':
    case 'mistake':
      return 'poor';
    default:
      return 'bad';
  }
}

function noteFor(quality: MoveQuality, rank: number): string {
  if (rank === 1) return 'This is the coach’s own first choice.';
  switch (quality) {
    case 'excellent':
      return 'As good as anything the coach found — there is more than one right answer here.';
    case 'good':
      return 'Sound, though the coach found something a little better.';
    case 'poor':
      return 'Playable, but there is a clearly better move on the board — worth finding before committing.';
    default:
      return 'This gives something away. Worth working out what, before playing it.';
  }
}

const ILLEGAL_NOTES: Record<string, string> = {
  occupied: 'There is already a stone here.',
  'off-board': 'That is not a point on the board.',
  suicide:
    'The stone would have no liberties left and would be captured the moment it was placed. The rules do not allow it.',
  ko: 'The ko rule forbids taking back here straight away. A threat played somewhere else first, once answered, makes this point playable again.',
  superko:
    'This would repeat a whole-board position that has already happened, which the rules forbid so that games have to end.',
  'game-over': 'The game is finished.',
  'wrong-turn': 'It is the other colour’s turn.',
};

/**
 * Grades every point `color` could hover over, best-relative.
 *
 * Computed for the whole board at once rather than per point, because the
 * ranking a grade depends on only exists once every move has been scored — and
 * because this runs behind a mouse, where a per-hover evaluation would be felt.
 */
export function gradeMoves(game: Game, color: Color): Map<Vertex, MoveFeedback> {
  const feedback = new Map<Vertex, MoveFeedback>();
  // Only the player on move can be graded. Asked about anyone else the engine
  // answers "wrong turn" for every point on the board, which would paint the
  // whole thing illegal and say nothing worth reading.
  if (game.isOver || color !== game.toPlay) return feedback;

  const ranked = evaluateMoves(game, color);
  const best = ranked[0]?.score ?? 0;

  ranked.forEach((candidate, index) => {
    const lostValue = Math.max(0, best - candidate.score);
    const quality = qualityFor(lostValue);
    feedback.set(candidate.vertex, {
      vertex: candidate.vertex,
      quality,
      label: LABELS[quality],
      note: noteFor(quality, index + 1),
      reason: candidate.reason,
      lostValue,
      rank: index + 1,
    });
  });

  // Whatever is left empty is empty for a reason the rules can state.
  for (let v = 0; v < game.position.area; v++) {
    if (game.position.at(v) !== 0 || feedback.has(v)) continue;
    const why = game.illegalReason({ type: 'play', vertex: v }, color) ?? 'occupied';
    feedback.set(v, {
      vertex: v,
      quality: 'illegal',
      label: LABELS.illegal,
      note: ILLEGAL_NOTES[why] ?? 'You cannot play here.',
      reason: '',
    });
  }

  return feedback;
}
